import React from 'react';
import { Box, Grid, Heading, VStack, Image, Flex, Text } from '@chakra-ui/react';
import { 
  presentIcon, 
  exchangeIcon, 
  feedbackIcon,
  presentImage,
  exchangeImage,
  feedbackImage,
  observerImage,
  observerGradientShape
} from '../../assets/figmaAssets';

function ProcessCard({ gradient, icon, iconAlt, title, image, imageAlt, imageStyle, imageTransform }) {
  return (
    <Box
      style={{ backgroundImage: gradient }}
      borderRadius="40px"
      h={{ base: "433px", md: "480px" }}
      position="relative"
      overflow="hidden"
    >
      {/* Icon + Title */}
      <VStack
        position="absolute"
        left="30px"
        top="29px"
        align="start"
        spacing="8px"
        zIndex={1}
      >
        <Image src={icon} alt={iconAlt} w="38px" h="38px" />
        <Heading
          as="h3"
          fontSize={{ base: "25px", md: "30px" }}
          fontWeight="bold"
          fontFamily="Inter, sans-serif"
          color="black"
          letterSpacing="0.1px"
          lineHeight="1.2"
        >
          {title}
        </Heading>
      </VStack>
      {/* Card Image */}
      <Image
        src={image}
        alt={imageAlt}
        loading="lazy"
        position="absolute"
        objectFit="cover"
        pointerEvents="none"
        transform={imageTransform}
        {...imageStyle}
      />
    </Box>
  );
}

export default function ProcessCards() {
  return (
    <Flex as="section"
    flexDirection={"column"}
    aria-label="Comment ça marche"
    alignItems={{base: "start", md: "center"}}
    paddingY={"40px"}
    gap={{ base: "20px", md: "40px" }}
    >
      <Flex 
      flexDirection={"column"}
      alignItems={{base: "start", md: "center"}}
      justifyContent={"center"}
      maxWidth={"493px"}
      gap={{ base: "10px", md: "20px" }}
      >
      <Heading
          as="h2"
          fontSize={{ base: "30px", md: "45px" }}
          fontWeight="bold"
          fontFamily="Inter, sans-serif"
          color="black"
          textAlign={{base: "start", md: "center"}}
          letterSpacing="0.1px"
          lineHeight="1.2"
        >
          Comment ça marche ?
        </Heading>
        <Text 
            fontSize={{ base: "14px", md: "20px" }}
            fontFamily="Inter, sans-serif"
            fontWeight="normal"
            color="black"
            letterSpacing="0.1px"
            maxW={{ base: "300px", md: "710px" }}
            lineHeight={{ base: "1.6", md: "1.4" }}
            textAlign={{base: "start", md: "center"}}
          >
           Rejoins un groupe de discussion autour du sujet de la semaine tous les jours à 19h.
          </Text>
      </Flex>
      <Flex w={"100%"} flexDirection={"column"} gap={"20px"}>
      <Grid
        w={"100%"}
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap="20px"
      >
        {/*
          Card 1 — "Présente ton exposé"
          Figma Desktop: left=-79, top=139, 545x428
          Figma Mobile:  left=-98, top=99,  545x428
        */}
        <ProcessCard
          gradient="linear-gradient(68.03deg, rgb(199, 229, 244) 33.25%, rgb(177, 240, 173) 90.87%, rgb(199, 229, 244) 205.96%)"
          icon={presentIcon}
          iconAlt="Icône de présentation"
          title={<>Présente<br />ton exposé</>}
          image={presentImage}
          imageAlt="Personne présentant son exposé devant un groupe"
          imageStyle={{
            bottom:  "-100px" ,
            w: "545px",
            h: "428px",
          }}
        />
        {/*
          Card 2 — "Échange avec les autres"
          Figma Desktop: left=-45, top=97, 480x480
          Figma Mobile:  left=-42, top=67, 446x446
        */}
        <ProcessCard
          gradient="linear-gradient(68.77deg, rgb(177, 240, 173) 18.66%, rgb(199, 229, 244) 37.82%, rgb(199, 229, 244) 135.33%)"
          icon={exchangeIcon}
          iconAlt="Icône d'échange"
          title={<>Échange avec<br />les autres</>}
          image={exchangeImage}
          imageAlt="Groupe de personnes échangeant leurs idées"
          imageStyle={{
            bottom:  "-70px" ,
            right: "0",
            w: { base: "446px", md: "480px" },
            h: { base: "446px", md: "480px" },
          }}
        />
        {/*
          Card 3 — "Prend des retours"
          Figma Desktop: left=0, bottom=0, 387x387, horizontally flipped
          Figma Mobile:  left=0, bottom=0, 387x387, horizontally flipped
        */}
        <ProcessCard
          gradient="linear-gradient(69deg, rgb(177, 240, 173) 49.52%, rgb(199, 229, 244) 66.54%)"
          icon={feedbackIcon}
          iconAlt="Icône de retours"
          title={<>Prend des<br />retours</>}
          image={feedbackImage}
          imageAlt="Personne recevant des retours constructifs"
          imageTransform="scaleX(-1)"
          imageStyle={{
            right: "0",
            bottom: "0",
            w: "387px",
            h: "387px",
          }}
        />
      </Grid>
      <Box
      w={"100%"}
      style={{ backgroundImage: "linear-gradient(59.14deg, rgb(225, 225, 225) 14.18%, rgb(186, 186, 186) 38.39%, rgb(225, 225, 225) 97.90%)" }}
      borderRadius="40px"
      overflow="hidden"
      position="relative"
      h={{ base: "395px", md: "298px" }}
    >
      {/*
        Desktop Figma spec:
        - Image at left=78px, top=15px, 198x287 (bleeds past 298px height)
        - Title at left=372px, top=77px, max-width 546px
        Mobile Figma spec:
        - Title at left=40px, top=35px, max-width 266px
        - Image at left=0, top=114px, 198x287
      */}

      {/* Image — absolute positioned to match Figma exactly */}
      <Image
        src={observerImage}
        alt="Personne qui observe et écoute une présentation"
        loading="lazy"
        position="absolute"
        left={{ base: "0", md: "78px" }}
        top={{ base: "auto", md: "15px" }}
        bottom={{ base: "0", md: "auto" }}
        w={{ base: "180px", md: "198px" }}
        h={{ base: "260px", md: "287px" }}
        objectFit="cover"
      />
      {/*
        Gradient shape overlay (Vector 12)
        Figma: x=83.34, y=15.70, w=193.16, h=286.96
        Sits on top of the image with a slight offset
      */}
      <Image
        src={observerGradientShape}
        alt=""
        position="absolute"
        left={{ base: "5px", md: "83px" }}
        top={{ base: "auto", md: "16px" }}
        bottom={{ base: "0", md: "auto" }}
        w={{ base: "175px", md: "193px" }}
        h={{ base: "260px", md: "287px" }}
        pointerEvents="none"
      />

      {/* Title — absolute positioned to match Figma */}
      <Heading
        as="h2"
        position="absolute"
        left={{ base: "40px", md: "372px" }}
        top={{ base: "35px", md: "77px" }}
        fontSize={{ base: "24px", md: "45px" }}
        fontWeight="bold"
        fontFamily="Inter, sans-serif"
        color="black"
        letterSpacing="0.1px"
        lineHeight="1.2"
        maxW={{ base: "266px", md: "546px" }}
      >
        Tu peux aussi observer et écouter !
      </Heading>
    </Box>
      </Flex>
    </Flex>
  );
}
